import { ReactNode } from "react";
import { Route, RouteObject } from "react-router-dom";
import RequireAuth from "@/components/Access/RequireAuth";
import RequireRole from "@/components/Access/RequireRole";
import { getEnabledManifests } from "./activeModules";
import { ModuleAudience, ModuleDashboardCard, ModuleNavItem } from "./types";

/** Guard-wrapped elements for an audience's contributed routes, keyed by path. */
function moduleRoutesFor(audience: ModuleAudience) {
  return getEnabledManifests()
    .flatMap((m) => m.routes ?? [])
    .filter((r) => r.audience === audience)
    .map((r) => {
      let element: ReactNode = <RequireAuth>{r.element}</RequireAuth>;
      if (r.roles?.length) {
        element = <RequireRole roles={r.roles}>{element}</RequireRole>;
      }
      return { path: r.path, element };
    });
}

/**
 * Build <Route> elements contributed by enabled modules for an audience. Returns
 * an array (not a component) so it can be spread directly as children of
 * react-router's <Routes>. Each route is wrapped in RequireAuth, and RequireRole
 * when the contribution declares roles — matching the host's existing route style.
 */
export function moduleRouteElements(audience: ModuleAudience) {
  return moduleRoutesFor(audience).map(({ path, element }) => (
    <Route key={path} path={path} element={element} />
  ));
}

/**
 * The same contributions as route objects, for the static tree in `src/router.tsx` —
 * <Route> JSX cannot be used there.
 */
export function moduleRouteObjects(audience: ModuleAudience): RouteObject[] {
  return moduleRoutesFor(audience);
}

/** Nav items contributed by enabled modules for an audience. */
export function moduleNavItemsFor(audience: ModuleAudience): ModuleNavItem[] {
  return getEnabledManifests()
    .flatMap((m) => m.navItems ?? [])
    .filter((n) => n.audience === audience);
}

/** Dashboard cards contributed by enabled modules for an audience. */
export function moduleDashboardCardsFor(
  audience: ModuleAudience,
): ModuleDashboardCard[] {
  return getEnabledManifests()
    .flatMap((m) => m.dashboardCards ?? [])
    .filter((c) => c.audience === audience);
}
